/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 10/26/2022
 * for userx-support
 */

import React, {useEffect, useRef, useState} from 'react';
import StatBlock from "./_ui/StatBlock";
import {formatCurrency} from "../_utilities/formattingUtils";
import {useUser} from "../_contexts/UserContext";
import {search} from "../_utilities/reactUtils";
import {useNavigate, useParams} from "react-router";
import TableBlock from "./_ui/TableBlock";
import supportAPI from "../_apis/supportAPI";
import {STEP} from "../system/defaults";
import useInterval from "../_hooks/useInterval";
import AgentTimeManager from "./_ui/AgentTimeManager";
import AgentSuggestedTickets from "./_ui/AgentSuggestedTickets";

const AgentDashboard = () => {
    const navigate = useNavigate();
    const params = useParams();
    const {userContext} = useUser();
    const agentId = params.agentId ? params.agentId : userContext.contact ? userContext.contact.contact_id : null;
    const activeTab = params.tab || 'tickets';
    const tabs = [
        { id: 'tickets', label: 'Tickets', classes: '' },
        { id: 'time', label: 'Time', classes: '' },
        { id: 'suggested', label: 'AI Suggested', classes: '' },
    ];

    const agentStepValue = calculateAgentStepValue(userContext.contact.meta.find(m => m.meta_key === 'pay_rate'));
    const ticketTotal = useRef(0);
    const overdueTotal = useRef(0);
    const dayTotal = useRef(0);
    const monthTotal = useRef(0);
    const ytdTotal = useRef(0);
    const [myAssigned, setMyAssigned] = useState([]);
    const [allowed, setAllowed] = useState(true);

    useEffect(() => {
        refresh()
    }, []);

    useInterval(() => {
        refresh();
    }, 5000);

    return (
        <div className={`d-flex flex-column h-100`}>
            <div className={`d-flex border-bottom flex-wrap`}>
                <StatBlock title={`Total`} number={ticketTotal.current}
                           classes={`border-end border-bottom bg-secondary text-white`}
                           style={{minWidth: '150px'}}/>
                <StatBlock title={`Time Today`} number={dayTotal.current}
                           classes={`border-end border-bottom bg-secondary text-white`}
                           style={{minWidth: '150px'}}/>
                <StatBlock title={`${new Date().toLocaleString("default", {month: "long"})} Income`}
                           number={formatCurrency(monthTotal.current)}
                           classes={`border-end border-bottom bg-secondary text-white`} style={{minWidth: '150px'}}/>
                <StatBlock title={`YTD Income`} number={formatCurrency(ytdTotal.current)}
                           classes={` border-bottom bg-secondary text-white`}
                           style={{minWidth: '150px'}}/>
            </div>

            <div className={`flex-grow-1 align-items-stretch p-3 m-3`}>
                <ul className="nav nav-tabs" role="tablist">
                    {tabs.map(tab => (
                        <li className={`nav-item ${tab.id === 'delete' ? `flex-grow-1 d-flex justify-content-end` : null}`}
                            key={tab.id} role="presentation"
                        >
                            <button
                                className={`nav-link ${tab.classes} ${activeTab === tab.id ? 'active' : ''}`}
                                type="button"
                                onClick={() => navigate(`/agents/${agentId}/${tab.id}`)}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="tab-content" id="companyTabContent">
                    <div className={`tab-pane fade ${activeTab==='tickets' ? 'active show' : null}`} id="tickets" role="tabpanel"
                         aria-labelledby="tickets-tab"
                         style={{border: '1px solid #ddd'}}>
                        <TableBlock title={`My Assigned Tickets`} classes={`table-mode bg-white`}
                                    items={myAssigned}/>
                    </div>
                    <div className={`p-3 bg-white tab-pane fade ${activeTab==='time' ? 'active show' : null}`} id="time" role="tabpanel"
                         aria-labelledby="time-tab"
                         style={{border: '1px solid #ddd'}}>
                        <AgentTimeManager agentId={agentId} />
                    </div>
                    <div className={`p-3 bg-white tab-pane fade ${activeTab==='suggested' ? 'active show' : null}`} id="suggested" role="tabpanel"
                         aria-labelledby="suggested-tab"
                         style={{border: '1px solid #ddd'}}>
                        <AgentSuggestedTickets agentId={agentId} initialHours={2} myAssigned={myAssigned}/>
                    </div>
                </div>
            </div>
        </div>
    );

    function calculateAgentStepValue(rateMeta) {
        if (rateMeta !== undefined) {
            return parseFloat(rateMeta.meta_value) / (60 / STEP);
        }
        return 0;
    }

    function refresh() {
        if (agentId) {
            // allowed to view page
            setAllowed(() => true);
            const agentId = params.agentId ? params.agentId : userContext.contact.contact_id;
            search('ticket', [
                `assignee_id = ${agentId}`,
                `(status_id <= 4 || status_id >= 7)`
            ], 'AND', 'priority_id ASC, date_due ASC').then(response => {
                // filter out Maintenance service tickets
                setMyAssigned(() => response);
            });
        } else {
            setAllowed(() => false);
        }

        // update stats
        ticketTotal.current = myAssigned.length;

        const currentDate = new Date();
        currentDate.setHours(0,0,0,0);
        overdueTotal.current = myAssigned.filter(t => {
            const dueDate = new Date(t.date_due);
            dueDate.setHours(0,0,0,0);
            return dueDate < currentDate;
        }).length;

        supportAPI.get(`/contact/${agentId}/currenttime`).then(response => {
            const rawDayTime = response.data.day_time ? parseInt(response.data.day_time.toString()) : 0;
            const dayMinutes = rawDayTime * STEP;
            dayTotal.current = Math.round((dayMinutes / 60) * 10) / 10;
            monthTotal.current = response.data.total_time ? parseInt(response.data.total_time.toString()) * agentStepValue : 0;
        });
        supportAPI.get(`/contact/${agentId}/ytdtime`).then(response => {
            ytdTotal.current = response.data.total_time ? parseInt(response.data.total_time.toString()) * agentStepValue : 0;
        });
    }
};

export default AgentDashboard;